import { useState, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { useSetAtom } from 'jotai';
import { auth, db } from '@/lib/firebase';
import { DEFAULT_USER_SETTINGS } from '@/constants';
import { User, UserSettings } from '@/types';
import { userAtom, isLoadingUserProfileAtom } from '@/stores/auth';

// Get user's timezone from browser
function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

interface AuthError {
  code: string;
  message: string;
}

interface UseAuthReturn {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

// Map Firebase error codes to user-friendly messages
function getErrorMessage(error: AuthError): string {
  switch (error.code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return error.message || 'An unexpected error occurred.';
  }
}

export function useAuth(): UseAuthReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setUser = useSetAtom(userAtom);
  const setIsLoadingUserProfile = useSetAtom(isLoadingUserProfileAtom);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      const authError = err as AuthError;
      setError(getErrorMessage(authError));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, displayName: string) => {
      setIsLoading(true);
      setError(null);
      setIsLoadingUserProfile(true);

      try {
        // Create the Firebase Auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const firebaseUser = userCredential.user;

        // Update the display name in Firebase Auth
        await updateProfile(firebaseUser, { displayName: displayName.trim() });

        // Get the user's timezone
        const timezone = getUserTimezone();

        // Create user document in Firestore with default settings
        const now = Timestamp.now();
        const userData: User = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || email.trim(),
          displayName: displayName.trim(),
          settings: DEFAULT_USER_SETTINGS as UserSettings,
          timezone,
          subscriptionTier: 'free',
          createdAt: now,
          updatedAt: now,
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), userData);

        // Immediately set the user data in the store to avoid delay
        setUser(userData);
        setIsLoadingUserProfile(false);
      } catch (err) {
        const authError = err as AuthError;
        setError(getErrorMessage(authError));
        setIsLoadingUserProfile(false);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [setUser, setIsLoadingUserProfile]
  );

  const signOut = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await firebaseSignOut(auth);
    } catch (err) {
      const authError = err as AuthError;
      setError(getErrorMessage(authError));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    signIn,
    signUp,
    signOut,
    isLoading,
    error,
    clearError,
  };
}
